import {useMemo} from 'react'
import {useB2BAgencyPacks} from './useB2BAgencyPacks.ts'
import {useAgencyActivity} from './useAgencyActivity.ts'
import {TripbookFeedback} from '../domain/entities/TripbookFeedback.ts'
import {PackTraveler} from '../domain/entities/B2BPackDetail.ts'

export type AgencyFeedbackEntry = {
    id: string
    traveler: PackTraveler
    feedback: TripbookFeedback
}

type FeedbackSource = {
    tripId: string
    tripbookId: string | null
    traveler: PackTraveler
    feedback: TripbookFeedback | null
}

function collectFeedback(rows: FeedbackSource[]): AgencyFeedbackEntry[] {
    return rows
        .filter((row): row is FeedbackSource & {feedback: TripbookFeedback} => !!row.feedback)
        .map((row) => ({id: row.tripbookId ?? row.tripId, traveler: row.traveler, feedback: row.feedback}))
        .sort((a, b) => (b.feedback.feedbackDate ?? '').localeCompare(a.feedback.feedbackDate ?? ''))
}

// Aggregates the per-tripbook feedback already embedded in each pack's
// activity (PrintedTripbook/CreatedTripbook.feedback) into a single agency-wide
// feed. There's no dedicated aggregated endpoint yet, so this rides on the
// same pack-detail queries already fetched for the "Packs y TripBooks" tab.
export function usePackagedAgencyFeedback(agencyId: string, isSelfService = false) {
    const {packs, isLoading} = useB2BAgencyPacks(agencyId, isSelfService)
    const feedbacks = useMemo(
        () => collectFeedback(packs.flatMap((p) => [...p.printed, ...p.created])),
        [packs]
    )
    return {feedbacks, isLoadingFeedbacks: isLoading}
}

// Same aggregation for pay-per-use agencies, sourced from the agency-wide
// activity endpoint instead of per-pack details.
export function usePayPerUseAgencyFeedback(agencyId: string, isSelfService = false) {
    const {activity, isLoadingActivity} = useAgencyActivity(agencyId, undefined, true, isSelfService)
    const feedbacks = useMemo(
        () => collectFeedback([...(activity?.printed ?? []), ...(activity?.created ?? [])]),
        [activity]
    )
    return {feedbacks, isLoadingFeedbacks: isLoadingActivity}
}
